import React, { useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { getSupervisorGroups, createNewSupGroup } from '../../../../redux/actions/settings/groups'
import { Toast as NotificationToast } from '../../tools'
import { handleTextPrepper } from '../../../../utils'

// MUI
import { makeStyles, withStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import StepConnector from '@material-ui/core/StepConnector'

// Forms
import SupGroupList from './SupGroupList'
import SupGroupNew from './SupGroupNew'
import SupGroupEdit from './SupGroupEdit'

// Icons
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn'
import GroupAddIcon from '@material-ui/icons/GroupAdd'
import ApartmentIcon from '@material-ui/icons/Apartment'
import CreateIcon from '@material-ui/icons/Create'
import { setGroupIndex, getOrgUnitInfo, getOrganizationalContacts } from '../../../../redux/actions'

const useStyles = makeStyles((theme) => ({
  greenBtn: {
    color: '#fff',
    backgroundColor: '#A4D164',
    '&:hover': {
      backgroundColor: '#8AB651'
    },
    '&:focus': {
      outline: 'none'
    }
  },
  deleteBtn: {
    color: '#fff',
    backgroundColor: '#A4D164',
    '&:hover': {
      backgroundColor: '#d31111'
    },
    '&:focus': {
      outline: 'none'
    }
  },
  backButton: {
    color: '#A4D164',
    borderColor: '#A4D164',
    '&:hover': {
      backgroundColor: '#e9ffcc'
    },
    '&:focus': {
      outline: 'none'
    }
  },
  saveButton: {
    color: '#fff',
    backgroundColor: '#5291fc',
    '&:hover': {
      backgroundColor: '#0f68f7'
    },
    '&:focus': {
      outline: 'none'
    }
  },
  userCard: {
    width: '100%'
  },
  listRoot: {
    width: '100%',
    position: 'relative',
    overflow: 'auto',
    maxHeight: 75
  },
  listOURoot: {
    width: '100%'
  },
  supervisorList: {
    width: '100%',
    position: 'relative',
    overflow: 'auto',
    maxHeight: theme.spacing(10)
  },
  stepperClassRoot: {
    backgroundColor: '#ccc',
    zIndex: 1,
    color: '#fff',
    width: 50,
    height: 50,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center'
  },
  stepperClassActive: {
    backgroundImage:
      'linear-gradient(0deg, rgba(195,242,93,1) 0%, rgba(82,145,252,1) 100%)',
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)'
  },
  stepperClassCompleted: {
    backgroundImage:
      'linear-gradient(0deg, rgba(195,242,93,1) 0%, rgba(82,145,252,1) 100%)'
  }
}))

const StepperConnector = withStyles({
  alternativeLabel: {
    top: 22
  },
  active: {
    '& $line': {
      backgroundImage:
        'linear-gradient(0deg, rgba(195,242,93,1) 0%, rgba(82,145,252,1) 100%)'
    }
  },
  completed: {
    '& $line': {
      backgroundImage:
        'linear-gradient(0deg, rgba(195,242,93,1) 0%, rgba(82,145,252,1) 100%)'
    }
  },
  line: {
    height: 3,
    border: 0,
    backgroundColor: '#eaeaf0',
    borderRadius: 1
  }
})(StepConnector)

const SupervisorGroups = (props) => {
  const classes = useStyles()

  // Provides the correct stepper icons in the connector
  const stepperIcons = (props) => {
    const { active, completed } = props

    const icons = {
      1: <CreateIcon />,
      2: <ApartmentIcon />,
      3: <GroupAddIcon />,
      4: <AssignmentTurnedInIcon />
    }

    return (
      <div
        className={clsx(classes.stepperClassRoot, {
          [classes.stepperClassActive]: active,
          [classes.stepperClassCompleted]: completed
        })}
      >
        {icons[String(props.icon)]}
      </div>
    )
  }

  stepperIcons.propTypes = {
    /**
     * Whether this step is active.
     */
    active: PropTypes.bool,
    /**
     * Mark the step as completed. Is passed to child components.
     */
    completed: PropTypes.bool
  }

  const dispatch = useDispatch()

  const TreeData = useSelector(state => state.auth.unitTree)
  const { groups: Groups = [] } = useSelector(state => state.supervisorGroups)
  const orgUnitInfo = useSelector(state => state.orgUnits.selectedUnit)
  // This pulls in the supervisors in order to match the alert author's supervisorID with a readble first name and last name for display
  const { availableContacts: orgSupervisors = [] } = useSelector(state => state.orgContacts)

  // The default object for the create group
  const blankGroup = {
    groupName: '',
    groupDesc: '',
    supervisors: [],
    unitID: ''
  }

  // Default object for the group list
  const newGroupList = [{
    accountID: '',
    createdAt: 0,
    deletedAt: 0,
    editedAt: 0,
    groupDesc: '',
    groupID: '',
    groupName: '',
    supervisors: [
      { supervisorID: '' }
    ],
    unitID: ''
  }]

  const [supervisorList, setSupervisorList] = useState([])
  const [groupEdit, setGroupEdit] = useState({})

  const [newGroupForm, setNewGroupForm] = useState(blankGroup)
  // Used in the list group component
  const [groupsList, setGroupsList] = useState(newGroupList)

  // State item for switching views
  const [supGroupForm, setSupGroupForm] = useState('list')

  // Get the supervisorList that containes all the ID's and first/last names
  useEffect(() => { dispatch(getSupervisorGroups()) }, [dispatch])
  useEffect(() => { dispatch(getOrganizationalContacts()) }, [dispatch])

  useMemo(() => { if (Groups && Array.isArray(Groups)) { setGroupsList(Groups) } }, [Groups])
  useMemo(() => {
    if (orgSupervisors && Boolean(orgSupervisors.length)) {
      const newSupervisorList = orgSupervisors.reduce((arr, supervisorInfo) => {
        if (supervisorInfo && supervisorInfo.supervisorID) {
          const { supervisorID, firstName, lastName } = supervisorInfo
          arr.push({ supervisorID, firstName, lastName })
        }
        return arr
      }, [])

      setSupervisorList(newSupervisorList)
    }
  }, [orgSupervisors])

  // Helper function to format supervisor name as 'lastName, firstName'
  const formatName = (firstName, lastName) => `${lastName}, ${firstName}`

  // Gets a formatted supervisor name from the ID provided
  const findSupervisorName = (supervisor) => {
    if (supervisorList && supervisorList.length) {
      const groupSupervisor = supervisorList.find((supervisorInfo) => supervisorInfo.supervisorID === supervisor)
      if (groupSupervisor) { return formatName(groupSupervisor.firstName, groupSupervisor.lastName) }
    }
    return ''
  }

  const fireCreateNewSuccess = () => {
    NotificationToast(false, 'Successfully Created New Group!')
    dispatch(setGroupIndex(0))
    setNewGroupForm(blankGroup)
    setSupGroupForm('list')
    dispatch(getSupervisorGroups())
  }

  const fireCreateNewFailure = () => {
    NotificationToast(true, 'Failed to Create New Group.')
  }

  const fireUpdateSuccess = () => {
    NotificationToast(false, 'Successfully Updated Group!')
    setSupGroupForm('list')
    setGroupEdit({})
    dispatch(getSupervisorGroups())
  }

  const fireUpdateFailure = () => {
    NotificationToast(true, 'Failed to Update Group.')
  }

  const fireDeleteGroupSuccess = () => {
    NotificationToast(false, 'Successfully Deleted Group!')
    setSupGroupForm('list')
    setGroupEdit({})
    dispatch(getSupervisorGroups())
  }

  const fireDeleteGroupFailure = () => {
    NotificationToast(true, 'Failed to Delete Group.')
  }

  const handleSaveNewGroup = () => {
    // Preps the privided information from the newGroupForm built in the group create into the expected object
    const info = {
      name: handleTextPrepper(newGroupForm.groupName),
      desc: handleTextPrepper(newGroupForm.groupDesc),
      supervisors: newGroupForm.supervisors,
      unitID: newGroupForm.unitID
    }
    // Double checks that no info has been omitted
    if (info.groupName !== '' || info.groupDesc !== '' || info.supervisors.length !== 0 || info.unitID !== '') {
      dispatch(createNewSupGroup(info, fireCreateNewSuccess, fireCreateNewFailure))
    }
  }

  // Used in the cancel button in create, and resets everything to default
  const handleBackTrack = () => {
    setSupGroupForm('list')
    setNewGroupForm(blankGroup)
    dispatch(setGroupIndex(0))
    setGroupEdit({})
    dispatch(getSupervisorGroups())
  }

  const handleEditGroup = (groupInfo) => {
    if (groupInfo && groupInfo.unitID) {
      dispatch(getOrgUnitInfo(groupInfo.unitID, () => { fireEditSuccess(groupInfo) }, fireEditFailure))
    } else {
      fireEditFailure()
    }
  }

  const fireEditSuccess = (groupInfo = {}) => {
    const {
      accountID, createdAt, deletedAt, editedAt, groupDesc = '', groupID, groupName = '', supervisors = [], unitID, unitName = ''
    } = groupInfo
    const newGroupInfo = {
      accountID, createdAt, deletedAt, editedAt, groupDesc, groupID, groupName, supervisors, unitID, unitName
    }

    setGroupEdit(newGroupInfo)
    setSupGroupForm('edit')
  }

  const fireEditFailure = () => { NotificationToast(true, 'Failed to edit group!') }

  // Used to switch to the correct form
  const FormSwap = () => {
    switch (supGroupForm) {
      case 'list':
        return (
          <SupGroupList
            classes={classes}
            setSupGroupForm={setSupGroupForm}
            groupsList={groupsList}
            findSupervisorName={findSupervisorName}
            setGroupEdit={setGroupEdit}
            orgUnitInfo={orgUnitInfo}
            handleEditGroup={handleEditGroup}
          />
        )
      case 'edit':
        return (
          <SupGroupEdit
            classes={classes}
            handleBackTrack={handleBackTrack}
            setSupGroupForm={setSupGroupForm}
            groupEdit={groupEdit}
            fireUpdateFailure={fireUpdateFailure}
            fireUpdateSuccess={fireUpdateSuccess}
            fireDeleteGroupFailure={fireDeleteGroupFailure}
            fireDeleteGroupSuccess={fireDeleteGroupSuccess}
          />
        )
      case 'new':
        return (
          <SupGroupNew
            classes={classes}
            setSupGroupForm={setSupGroupForm}
            handleBackTrack={handleBackTrack}
            stepperIcons={stepperIcons}
            StepperConnector={StepperConnector}
            TreeData={TreeData}
            orgUnitInfo={orgUnitInfo}
            setNewGroupForm={setNewGroupForm}
            newGroupForm={newGroupForm}
            handleSaveNewGroup={handleSaveNewGroup}
          />
        )
      default:
        return (<></>)
    }
  }

  return (
    <Grid container direction='row'>
      <Grid item container direction='column' spacing={2} className='m-auto w-100'>
        <Grid container item direction='row'>
          <Grid item>
            <h2 className='my-auto'>
              Supervisor Groups
            </h2>
          </Grid>
        </Grid>

        <Grid container item direction='row' className='px-0'>
          <FormSwap />
        </Grid>
      </Grid>
    </Grid>
  )
}

export default SupervisorGroups
